import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LoginButton } from '../auth/LoginButton';
import { LogoutButton } from '../auth/LogoutButton';
import { useAuthRoles } from '../../hooks/useAuthRoles';
import { useInvitationService } from '../../services/invitation.service';
import UserAccountMenu from './UserAccountMenu';
import { motion } from 'framer-motion';
import { CircleHelp, LayoutDashboard, UserRoundCog, Menu, X } from 'lucide-react';
import { ONBOARDING_STORAGE_KEYS, PRODUCT_TOUR_RESTART_EVENT } from '../../constants/onboarding';
import { GUIDED_TOUR_ENABLED } from '../../constants/featureFlags';
import { Button } from '@/components/ui/atoms/Button';
import { IconWrapper } from '../ui/atoms';
import { useProductOnboarding } from '../../hooks/useProductOnboarding';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, isLoading } = useAuth0();
  const { isAdmin } = useAuthRoles();
  const { isInvited } = useInvitationService();
  const location = useLocation();
  const { getFlag, ready: onboardingReady } = useProductOnboarding();

  // const [userIsInvited, setUserIsInvited] = useState(false);

  useEffect(() => {
    const checkInvitation = async () => {
      if (isAuthenticated && !isAdmin) {
        // const invitationStatus = await isInvited();
        // setUserIsInvited(invitationStatus);
      }
    };
    checkInvitation();
  }, [isInvited, isAuthenticated, isAdmin]);

  // Helper function to check if a path is active
  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const dispatchRestartProductTour = () => {
    window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_RESTART_EVENT));
  };

  /** Help control is introduced in the job-details leg; show once dashboard or JD tour is done (persisted). */
  const showRestartTourButton =
    GUIDED_TOUR_ENABLED &&
    onboardingReady &&
    isAuthenticated &&
    !isLoading &&
    (getFlag(ONBOARDING_STORAGE_KEYS.dashboardTourDone) ||
      getFlag(ONBOARDING_STORAGE_KEYS.jobDetailsTourDone));
  const brandLogoSrc = '/logo/dark-logo.png';
  const brandName = 'Invirtus';
  const brandLogoAlt = 'Invirtus';

  return (
    <header className="bg-white text-dark-invirtus border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8z">
        <div className="flex justify-between items-center py-4">
          {/* Logo and brand */}
          <Link to="/dashboard" className="flex items-center gap-2 group" id="header-logo-link">
            <motion.img
              initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              whileHover={{ scale: 1.1, rotate: 5 }}
              src={brandLogoSrc}
              alt={brandLogoAlt}
              className="h-10 w-auto max-w-24 sm:max-w-28 relative object-contain"
            />
            <span className="text-dark-invirtus text-3xl font-bold text-center">
              {brandName}
            </span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {isAuthenticated && !isLoading && isAdmin && (
              <>
                <Link
                  to="/dashboard"
                  className={`font-semibold text-sm px-3.5 py-1.5 rounded-full transition-all duration-200 ${isActivePath('/dashboard')
                    ? 'bg-gradient-purple/70 text-dark-invirtus shadow-sm'
                    : 'text-dark-invirtus/90 hover:text-dark-invirtus hover:bg-gray-100 focus:bg-gray-100 focus:text-dark-invirtus'
                    }`}
                  id="header-dashboard-link"
                >
                  Dashboard
                </Link>
                <Link
                  to="/admin"
                  className={`font-semibold text-sm px-3.5 py-1.5 rounded-full transition-all duration-200 ${isActivePath('/admin')
                    ? 'bg-gradient-purple/70 text-dark-invirtus shadow-sm'
                    : 'text-dark-invirtus/90 hover:text-dark-invirtus hover:bg-gray-100 focus:bg-gray-100 focus:text-dark-invirtus'
                    }`}
                  id="header-admin-link"
                >
                  Admin
                </Link>
              </>
            )}
          </nav>

          {/* CTA: tour help (all breakpoints) + desktop auth */}
          <div className="flex items-center gap-2">
            {showRestartTourButton && (
              <Button
                type="icon"
                intent="muted"
                icon={<IconWrapper size="sm"><CircleHelp /></IconWrapper>}
                title="Restart guided tour"
                data-tour="help-restart-tour"
                aria-label="Restart guided tour"
                onClick={dispatchRestartProductTour}
              />
            )}
            <div className="hidden md:flex items-center gap-4">
              {!isLoading && (
                <>
                  {!isAuthenticated ? (
                    <LoginButton origin="header" />
                  ) : (
                    <UserAccountMenu />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <Button
            type="icon"
            intent="muted"
            size="md"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            id="mobile-menu-toggle"
            icon={isMenuOpen ? <X /> : <Menu />}
          />
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            {isAuthenticated && (
              <>
                {/* User Info */}
                <div className="flex items-center gap-3 px-3 py-2 mb-4">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-purple text-dark-invirtus flex items-center justify-center text-sm font-medium">
                      {user?.name ? user.name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2) : 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-dark-invirtus font-medium truncate">{user?.name || 'User'}</p>
                    <p className="text-dark-invirtus/60 text-sm truncate">{user?.email}</p>
                  </div>
                </div>
              </>
            )}

            {/* Navigation Links */}
            <nav className="flex flex-col gap-2 mb-4 px-3">
              {isAuthenticated && !isLoading && isAdmin && (
                <>
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isActivePath('/dashboard')
                      ? 'bg-gradient-purple text-dark-invirtus font-semibold shadow-md border-b-2 border-primary'
                      : 'text-dark-invirtus/80 hover:text-dark-invirtus hover:bg-gray-100 focus:bg-gray-100 focus:text-dark-invirtus'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                    id="mobile-dashboard-link"
                  >
                    <LayoutDashboard />
                    Dashboard
                  </Link>

                  <Link
                    to="/admin"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${isActivePath('/admin')
                      ? 'bg-gradient-purple text-dark-invirtus font-semibold shadow-md border-b-2 border-primary'
                      : 'text-dark-invirtus/80 hover:text-dark-invirtus hover:bg-gray-100 focus:bg-gray-100 focus:text-dark-invirtus'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                    id="mobile-admin-link"
                  >
                    <UserRoundCog />
                    Admin Panel
                  </Link>
                </>
              )}
            </nav>

            {/* User Section */}
            <div className="border-t border-gray-200 pt-4">
              {!isAuthenticated ? (
                <LoginButton origin="mobile_menu" />
              ) : (
                <div className="flex flex-col gap-3">
                  {/* Logout Button */}
                  <LogoutButton variant="mobile" origin="mobile_menu" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
} 
